import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { animate } from 'animejs';
import { getNotasFiscais, matchNotaFiscal } from './notaFiscal';
import './GlobalSearch.css';

/**
 * GlobalSearch - Busca global estilo Cmd+K
 * Busca em requisições, POCs, fornecedores, etc
 */
export default function GlobalSearch({ onClose, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all'); // all, requisicoes, fornecedores, pocs
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Categorias de busca
  const categories = [
    { id: 'all', label: 'Tudo', icon: '🔍' },
    { id: 'requisicoes', label: 'Requisições', icon: '📋' },
    { id: 'pocs', label: 'POCs', icon: '📄' },
    { id: 'fornecedores', label: 'Fornecedores', icon: '🏢' }
  ];

  // Busca com debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const searchResults = await performSearch(query, category);
        setResults(searchResults);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Erro na busca:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, category]);

  // Função de busca (integrar com sua API)
  const performSearch = async (searchQuery, searchCategory) => {
    const results = [];
    
    try {
      // Busca em requisições (endpoint existente)
      if (searchCategory === 'all' || searchCategory === 'requisicoes') {
        const reqResponse = await fetch(`/requisicoes/lite?q=${encodeURIComponent(searchQuery)}&limit=200`);
        if (reqResponse.ok) {
          const reqData = await reqResponse.json();
          const q = (searchQuery || '').toLowerCase();
          // filtro client-side: PRC, solicitante, centro de custo, fornecedor e nº da NF
          const reqFiltered = (Array.isArray(reqData) ? reqData : []).filter((r) => {
            const nfs = getNotasFiscais(r);
            const camposNf = nfs.map(nf => `${nf.fornecedor || ''} ${nf.numero || ''}`).join(' ');
            const campos = `${r.header?.requisicao || ''} ${r.header?.solicitante || ''} ${r.header?.solicitanteNome || ''} ${r.header?.centroCusto || ''} ${r.compra?.fornecedor || ''} ${camposNf}`;
            return campos.toLowerCase().includes(q) || matchNotaFiscal(r, searchQuery);
          });
          results.push(...reqFiltered.slice(0, 20).map(r => ({
            type: 'requisicao',
            id: r.id,
            title: `Requisição ${r.header?.requisicao || r.id}`,
            subtitle: `${r.header?.solicitante} • ${formatMoney(r.totalGeral, r.header?.moeda)}`,
            icon: '📋',
            data: r,
            action: () => onNavigate?.('requisicao', r.id)
          })));
        }
      }

      // Busca em fornecedores (endpoint existente)
      if (searchCategory === 'all' || searchCategory === 'fornecedores') {
        const fornResponse = await fetch(`/fornecedores/?busca=${encodeURIComponent(searchQuery)}&ativo=true`);
        if (fornResponse.ok) {
          const fornPayload = await fornResponse.json();
          const fornData = Array.isArray(fornPayload) ? fornPayload : (fornPayload?.fornecedores || []);
          results.push(...fornData.map(f => ({
            type: 'fornecedor',
            id: f.id,
            title: f.nome,
            subtitle: `${f.email || ''} • ${f.telefone || ''}`,
            icon: '🏢',
            data: f,
            action: () => onNavigate?.('fornecedor', f.id)
          })));
        }
      }

      // Busca em POCs (sem endpoint search no backend: usa lite e filtra local)
      if (searchCategory === 'all' || searchCategory === 'pocs') {
        const pocResponse = await fetch(`/compras/lite`);
        if (pocResponse.ok) {
          const pocData = await pocResponse.json();
          const q = (searchQuery || '').toLowerCase();
          const pocFiltered = (Array.isArray(pocData) ? pocData : []).filter((p) => {
            const numeroPedido = String(p?.compra?.numeroPedido || '').toLowerCase();
            const requisicao = String(p?.header?.requisicao || '').toLowerCase();
            const status = String(p?.status || '').toLowerCase();
            const nfs = getNotasFiscais(p);
            return numeroPedido.includes(q) || requisicao.includes(q) || status.includes(q)
              || nfs.some(nf => (nf.numero || '').toLowerCase().includes(q)) || matchNotaFiscal(p, searchQuery);
          });
          results.push(...pocFiltered.map(p => ({
            type: 'poc',
            id: p.id,
            title: `POC ${p.compra?.numeroPedido || p.id}`,
            subtitle: `Req ${p.header?.requisicao} • ${p.status}`,
            icon: '📄',
            data: p,
            action: () => onNavigate?.('poc', p.id)
          })));
        }
      }
    } catch (error) {
      console.error('Erro ao buscar:', error);
    }

    return results.slice(0, 10); // Limita a 10 resultados
  };

  const formatMoney = (value, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency === 'BRL' || currency === 'R$' ? 'BRL' : 'USD'
    }).format(value || 0);
  };

  // Navegação por teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            results[selectedIndex].action?.();
            onClose?.();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose?.();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [results, selectedIndex, onClose]);

  // Animação de entrada
  useEffect(() => {
    if (modalRef.current) {
      animate(modalRef.current, {
        opacity: [0, 1],
        scale: [0.95, 1],
        duration: 200,
        easing: 'out-quad'
      });
    }
    inputRef.current?.focus();
  }, []);

  // Scroll automático para item selecionado
  useEffect(() => {
    const selected = document.querySelector('.global-search-result.selected');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  return createPortal(
    <div className="global-search-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="global-search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Busca global"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com input */}
        <div className="global-search-header">
          <span className="global-search-icon" aria-hidden="true">🔍</span>
          <input
            ref={inputRef}
            type="text"
            className="global-search-input"
            placeholder="Buscar em tudo... (Esc para fechar)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
            aria-label="Campo de busca global"
            aria-autocomplete="list"
            aria-controls="global-search-results"
          />
          {loading && <span className="global-search-loading">⏳</span>}
        </div>

        {/* Filtros de categoria */}
        <div className="global-search-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`global-search-category ${category === cat.id ? 'active' : ''}`}
              onClick={() => setCategory(cat.id)}
            >
              <span className="category-icon">{cat.icon}</span>
              <span className="category-label">{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Resultados */}
        <div id="global-search-results" className="global-search-results" role="listbox" aria-label="Resultados da busca">
          {!query && (
            <div className="global-search-empty">
              <span className="empty-icon">💡</span>
              <p>Digite para buscar em requisições, POCs, fornecedores...</p>
              <div className="search-tips">
                <span className="tip">💡 Dica: Use ↑↓ para navegar, Enter para abrir</span>
              </div>
            </div>
          )}

          {query && !loading && results.length === 0 && (
            <div className="global-search-empty">
              <span className="empty-icon">😕</span>
              <p>Nenhum resultado encontrado para "{query}"</p>
            </div>
          )}

          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              className={`global-search-result ${selectedIndex === index ? 'selected' : ''}`}
              onClick={() => {
                result.action?.();
                onClose?.();
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="result-icon">{result.icon}</span>
              <div className="result-content">
                <div className="result-title">{result.title}</div>
                <div className="result-subtitle">{result.subtitle}</div>
              </div>
              <span className="result-type">{result.type}</span>
            </div>
          ))}
        </div>

        {/* Footer com atalhos */}
        <div className="global-search-footer">
          <div className="footer-shortcuts">
            <span><kbd>↑↓</kbd> Navegar</span>
            <span><kbd>Enter</kbd> Abrir</span>
            <span><kbd>Esc</kbd> Fechar</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
